import React, { type PropsWithChildren, useCallback, useEffect } from 'react';
import { type ViewProps } from 'react-native';
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withTiming,
  type WithTimingConfig,
} from 'react-native-reanimated';

// ─────────────────────────────────────────────────────────────────────────────

export const UpsellAnimatedWrapper = (props: UpsellAnimatedWrapperProps) => {
  const { animationDelay, children, style: propStyles, ...restProps } = props;

  const translateYSV = useSharedValue(30);
  const opacitySV = useSharedValue(0);

  // ─── Helpers ─────────────────────────────────────────────────────────

  const startAnimations = useCallback(() => {
    translateYSV.value = withDelay(
      animationDelay,
      withTiming(0, TRANSLATE_ANIMATION_CONFIG),
    );

    opacitySV.value = withDelay(
      animationDelay,
      withTiming(1, OPACITY_ANIMATION_CONFIG),
    );
  }, [animationDelay, opacitySV, translateYSV]);

  // ─── Effects ─────────────────────────────────────────────────────────

  useEffect(startAnimations, [startAnimations]);

  // ─── Styles ──────────────────────────────────────────────────────────

  const animatedStyle = useAnimatedStyle(() => {
    return {
      transform: [{ translateY: translateYSV.value }],
      opacity: opacitySV.value,
    };
  });

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <Animated.View style={[animatedStyle, propStyles]} {...restProps}>
      {children}
    </Animated.View>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const ANIMATIONS_DURATION = 500;

const TRANSLATE_ANIMATION_CONFIG: WithTimingConfig = {
  duration: ANIMATIONS_DURATION,
  easing: Easing.inOut(Easing.quad),
};
const OPACITY_ANIMATION_CONFIG: WithTimingConfig = {
  duration: ANIMATIONS_DURATION,
  easing: Easing.inOut(Easing.quad),
};

// ─── Types ───────────────────────────────────────────────────────────────────

type UpsellAnimatedWrapperProps = PropsWithChildren<{
  animationDelay: number;
}> &
  ViewProps;
